import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";

export type WebContextGuideId =
  | "domain_path"
  | "mode_path"
  | "practice_intro"
  | "vocabulary_intro"
  | "missions_intro"
  | "mixed_missions_intro";

interface GuideStep {
  title: string;
  body: string;
}

interface ContextGuideOverlayProps {
  doneLabel: string;
  guideId: WebContextGuideId;
  nextLabel: string;
  onDismiss: () => void;
  selectors: string[];
  skipLabel: string;
  stepLabel: string;
  steps: readonly GuideStep[];
  title: string;
}

const STORAGE_KEY = "wordinarius_echo.web.context_guides.v1";
const GUIDE_IDS: WebContextGuideId[] = [
  "domain_path",
  "mode_path",
  "practice_intro",
  "vocabulary_intro",
  "missions_intro",
  "mixed_missions_intro",
];

function readDismissedGuides(): Record<WebContextGuideId, boolean> {
  const fallback = Object.fromEntries(GUIDE_IDS.map((id) => [id, false])) as Record<WebContextGuideId, boolean>;
  if (typeof window === "undefined") {
    return fallback;
  }
  try {
    const value = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "null") as Partial<Record<WebContextGuideId, boolean>> | null;
    return Object.fromEntries(GUIDE_IDS.map((id) => [id, value?.[id] === true])) as Record<WebContextGuideId, boolean>;
  } catch {
    return fallback;
  }
}

export function useWebContextGuides() {
  const [dismissed, setDismissed] = useState(readDismissedGuides);
  const persist = (next: Record<WebContextGuideId, boolean>) => {
    setDismissed(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Keep guide state in memory when browser policy blocks persistence.
    }
  };
  return {
    dismissed,
    dismiss: (guideId: WebContextGuideId) => persist({ ...dismissed, [guideId]: true }),
    restart: () => persist(Object.fromEntries(GUIDE_IDS.map((id) => [id, false])) as Record<WebContextGuideId, boolean>),
  };
}

export function ContextGuideOverlay(props: ContextGuideOverlayProps) {
  const [stepIndex, setStepIndex] = useState(0);
  const [spotlightStyle, setSpotlightStyle] = useState<CSSProperties | null>(null);
  const [cardStyle, setCardStyle] = useState<CSSProperties>({});
  const cardRef = useRef<HTMLElement>(null);
  const safeStepIndex = Math.min(stepIndex, Math.max(0, props.steps.length - 1));
  const step = props.steps[safeStepIndex];
  const isLast = safeStepIndex >= props.steps.length - 1;

  const measure = useCallback(() => {
    const selector = props.selectors[safeStepIndex] || props.selectors[0];
    const target = selector ? document.querySelector<HTMLElement>(selector) : null;
    if (!target) {
      setSpotlightStyle(null);
      setCardStyle({});
      return;
    }
    const viewportWidth = window.visualViewport?.width || window.innerWidth;
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    const rect = target.getBoundingClientRect();
    const padding = 8;
    const left = Math.max(8, rect.left - padding);
    const top = Math.max(8, rect.top - padding);
    const right = Math.min(viewportWidth - 8, rect.right + padding);
    const bottom = Math.min(viewportHeight - 8, rect.bottom + padding);
    const cardWidth = Math.min(420, viewportWidth - 24);
    const cardHeight = cardRef.current?.getBoundingClientRect().height || 260;
    const edgeInset = 8;
    const preferredGap = 14;
    const minimumGap = 6;
    const belowSpace = Math.max(0, viewportHeight - edgeInset - bottom);
    const aboveSpace = Math.max(0, top - edgeInset);
    const placeBelow = belowSpace >= cardHeight + minimumGap || belowSpace >= aboveSpace;
    const availableSpace = placeBelow ? belowSpace : aboveSpace;
    const gap = Math.min(preferredGap, Math.max(minimumGap, availableSpace - cardHeight));
    const maxCardHeight = Math.max(1, availableSpace - gap);
    const renderedCardHeight = Math.min(cardHeight, maxCardHeight);
    const cardTop = placeBelow
      ? bottom + gap
      : top - gap - renderedCardHeight;

    setSpotlightStyle({
      height: Math.max(1, bottom - top),
      left,
      top,
      width: Math.max(1, right - left),
    });
    setCardStyle({
      left: Math.max(12, (viewportWidth - cardWidth) / 2),
      maxHeight: maxCardHeight,
      top: cardTop,
      width: cardWidth,
    });
  }, [props.guideId, props.selectors, safeStepIndex]);

  useLayoutEffect(() => {
    const selector = props.selectors[safeStepIndex] || props.selectors[0];
    const target = selector ? document.querySelector<HTMLElement>(selector) : null;
    target?.scrollIntoView({ block: "center", behavior: "auto" });
    const frame = window.requestAnimationFrame(measure);
    const resizeObserver = new ResizeObserver(measure);
    if (cardRef.current) {
      resizeObserver.observe(cardRef.current);
    }
    const onViewportChange = () => measure();
    window.addEventListener("resize", onViewportChange);
    window.addEventListener("scroll", onViewportChange, true);
    window.visualViewport?.addEventListener("resize", onViewportChange);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", onViewportChange);
      window.removeEventListener("scroll", onViewportChange, true);
      window.visualViewport?.removeEventListener("resize", onViewportChange);
      resizeObserver.disconnect();
    };
  }, [measure, props.selectors, safeStepIndex]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(measure);
    return () => window.cancelAnimationFrame(frame);
  }, [measure, step]);

  if (!step) {
    return null;
  }
  const next = () => {
    if (isLast) {
      props.onDismiss();
    } else {
      setStepIndex((current) => current + 1);
    }
  };

  return (
    <div className="context-guide-layer">
      <button type="button" className="context-guide-backdrop" aria-label={props.nextLabel} onClick={next} />
      {spotlightStyle ? <div className="context-guide-spotlight" style={spotlightStyle} aria-hidden="true" /> : null}
      <section ref={cardRef} style={cardStyle} className="context-guide-card" role="dialog" aria-modal="true" aria-labelledby="context-guide-title">
        <span>{props.stepLabel} {safeStepIndex + 1}/{props.steps.length}</span>
        <h2 id="context-guide-title">{props.title}</h2>
        <strong>{step.title}</strong>
        <p>{step.body}</p>
        <div className="context-guide-dots" aria-hidden="true">
          {props.steps.map((item, index) => <i key={`${item.title}-${index}`} className={index === safeStepIndex ? "is-active" : ""} />)}
        </div>
        <div className="context-guide-actions">
          <button type="button" className="text-button" onClick={props.onDismiss}>{props.skipLabel}</button>
          <button type="button" className="primary-button" onClick={next}>{isLast ? props.doneLabel : props.nextLabel}</button>
        </div>
      </section>
    </div>
  );
}

export const WEB_CONTEXT_GUIDE_STORAGE_KEY = STORAGE_KEY;
